import { WildfireChangelog } from 'features/wildfires/components/WildfireChangelog';
import { useFormContext } from 'react-hook-form';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EvacZone, RegionEvacZoneStyle } from 'shared/types';
import { GeoEventFormValues } from '../../GeoEventForm.types';
import { EvacZoneStyles } from '../../../../../../constants';
import { hasRegionsChanged } from '../../GeoEventForm.utils';

type FormChangelogProps = {
  geoEvent: Wildfire;
  regionEvacZones: EvacZone[];
};

export const FormChangelog = (props: FormChangelogProps): JSX.Element => {
  const { geoEvent, regionEvacZones } = props;
  const { t } = useTranslation();
  const { watch } = useFormContext<GeoEventFormValues>();

  const [name, address, lat, lng, regions] = watch([
    'name',
    'address',
    'lat',
    'lng',
    'regions',
  ]);

  const evacZoneStyle: RegionEvacZoneStyle =
    geoEvent?.regions[0]?.evacZoneStyle ||
    regionEvacZones[0]?.region.evacZoneStyle ||
    EvacZoneStyles.default;

  const nameChanged = geoEvent?.name?.trim() !== name.trim();
  const addressChanged =
    !geoEvent?.data.isComplexParent &&
    geoEvent?.address?.trim() !== address.trim();
  const latChanged = geoEvent?.lat !== lat;
  const lngChanged = geoEvent?.lng !== lng;
  const regionsChanged =
    !geoEvent?.data.isComplexParent &&
    hasRegionsChanged(geoEvent?.regions ?? [], regions);

  const incidentInfoChanged =
    Boolean(geoEvent) &&
    (nameChanged ||
      addressChanged ||
      latChanged ||
      lngChanged ||
      regionsChanged);

  return (
    <WildfireChangelog
      wildfire={geoEvent}
      evacZones={regionEvacZones}
      zoneStyle={evacZoneStyle}
    >
      {incidentInfoChanged && (
        <>
          {nameChanged && (
            <Typography>
              {t('geoEventChangelog.name', {
                prevValue: geoEvent.name,
                newValue: name,
              })}
            </Typography>
          )}
          {addressChanged && (
            <Typography>
              {t('geoEventChangelog.address', {
                prevValue: geoEvent.address,
                newValue: address,
              })}
            </Typography>
          )}
          {latChanged && (
            <Typography>
              {t('geoEventChangelog.latitude', {
                prevValue: geoEvent.lat.toFixed(6),
                newValue: lat.toFixed(6),
              })}
            </Typography>
          )}
          {lngChanged && (
            <Typography>
              {t('geoEventChangelog.longitude', {
                prevValue: geoEvent.lng.toFixed(6),
                newValue: lng.toFixed(6),
              })}
            </Typography>
          )}
          {regionsChanged && (
            <Typography>{t('geoEventChangelog.regions')}</Typography>
          )}
        </>
      )}
    </WildfireChangelog>
  );
};
